// utils/sanitize.js
// Input sanitization utilities to prevent XSS and other injection attacks

/**
 * Escapes HTML special characters to prevent XSS
 * @param {string} str - The string to escape
 * @returns {string} - The escaped string
 */
export function escapeHtml(str) {
  if (!str || typeof str !== 'string') return '';
  
  const htmlEscapes = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
    '`': '&#x60;',
    '=': '&#x3D;'
  };
  
  return str.replace(/[&<>"'`=/]/g, char => htmlEscapes[char]);
}

/**
 * Validates and sanitizes a URL
 * @param {string} url - The URL to validate
 * @returns {string|null} - The sanitized URL or null if invalid
 */
export function sanitizeUrl(url) {
  if (!url || typeof url !== 'string') return null;
  
  try {
    const parsed = new URL(url);
    
    // Only allow http, https, and wss protocols
    const allowedProtocols = ['http:', 'https:', 'wss:'];
    if (!allowedProtocols.includes(parsed.protocol)) {
      return null;
    }
    
    // Block dangerous URL schemes (security check, not eval usage)
    // eslint-disable-next-line no-script-url
    if (url.toLowerCase().includes('javascript:') || 
        url.toLowerCase().includes('data:') ||
        url.toLowerCase().includes('vbscript:')) {
      return null;
    }
    
    return parsed.href;
  } catch {
    return null;
  }
}

/**
 * Validates a WebSocket relay URL
 * @param {string} url - The relay URL to validate
 * @returns {boolean} - True if valid relay URL
 */
export function isValidRelayUrl(url) {
  if (!url || typeof url !== 'string') return false;
  
  try {
    const parsed = new URL(url);
    
    // Must be wss:// (secure WebSocket) in production
    // Allow ws:// only for localhost development
    if (parsed.protocol === 'wss:') return true;
    if (parsed.protocol === 'ws:' && 
        (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1')) {
      return true;
    }
    
    return false;
  } catch {
    return false;
  }
}

/**
 * Sanitizes user-provided content before display
 * Removes potential script injections while preserving safe content
 * @param {string} content - The content to sanitize
 * @returns {string} - The sanitized content
 */
export function sanitizeContent(content) {
  if (!content || typeof content !== 'string') return '';
  
  // Remove any HTML tags
  let sanitized = content.replace(/<[^>]*>/g, '');
  
  // Remove potential script injections in URLs
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/vbscript:/gi, '');
  sanitized = sanitized.replace(/data:/gi, '');
  
  // Remove event handlers that might be injected
  sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  
  return sanitized;
}

/**
 * Validates that a pubkey is in the correct format
 * @param {string} pubkey - The public key to validate
 * @returns {boolean} - True if valid format
 */
export function isValidPubkey(pubkey) {
  if (!pubkey || typeof pubkey !== 'string') return false;
  // Public keys should be 64 hex characters
  return /^[0-9a-fA-F]{64}$/.test(pubkey);
}

/**
 * Validates an event ID format
 * @param {string} eventId - The event ID to validate
 * @returns {boolean} - True if valid format
 */
export function isValidEventId(eventId) {
  if (!eventId || typeof eventId !== 'string') return false;
  // Event IDs should be 64 hex characters
  return /^[0-9a-fA-F]{64}$/.test(eventId);
}

/**
 * Sanitizes metadata from nostr profiles
 * @param {object} metadata - The metadata object
 * @returns {object} - The sanitized metadata
 */
export function sanitizeMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object') return {};
  
  const sanitized = {};
  
  // Sanitize text fields
  if (metadata.name) {
    sanitized.name = sanitizeContent(String(metadata.name).slice(0, 100));
  }
  if (metadata.display_name) {
    sanitized.display_name = sanitizeContent(String(metadata.display_name).slice(0, 100));
  }
  if (metadata.about) {
    sanitized.about = sanitizeContent(String(metadata.about).slice(0, 1000));
  }
  if (metadata.nip05) {
    // NIP-05 should be in format user@domain.com
    const nip05 = String(metadata.nip05).slice(0, 200);
    if (/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(nip05)) {
      sanitized.nip05 = nip05;
    }
  }
  
  // Sanitize URL fields
  if (metadata.picture) {
    const sanitizedUrl = sanitizeUrl(String(metadata.picture));
    if (sanitizedUrl) {
      sanitized.picture = sanitizedUrl;
    }
  }
  if (metadata.banner) {
    const sanitizedUrl = sanitizeUrl(String(metadata.banner));
    if (sanitizedUrl) {
      sanitized.banner = sanitizedUrl;
    }
  }
  if (metadata.website) {
    const sanitizedUrl = sanitizeUrl(String(metadata.website));
    if (sanitizedUrl) {
      sanitized.website = sanitizedUrl;
    }
  }
  if (metadata.lud16) {
    // Lightning address format
    const lud16 = String(metadata.lud16).slice(0, 200);
    if (/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(lud16)) {
      sanitized.lud16 = lud16;
    }
  }
  
  return sanitized;
}

/**
 * Rate limiter for actions to prevent abuse
 */
class RateLimiter {
  constructor(maxActions, windowMs) {
    this.maxActions = maxActions;
    this.windowMs = windowMs;
    this.actions = new Map();
  }
  
  /**
   * Check if an action is allowed
   * @param {string} key - Unique identifier for the action/user
   * @returns {boolean} - True if action is allowed
   */
  isAllowed(key) {
    const now = Date.now();
    const windowStart = now - this.windowMs;
    
    // Get existing actions for this key
    let actionTimes = this.actions.get(key) || [];
    
    // Filter to only actions within the window
    actionTimes = actionTimes.filter(time => time > windowStart);
    
    if (actionTimes.length >= this.maxActions) {
      return false;
    }
    
    // Record this action
    actionTimes.push(now);
    this.actions.set(key, actionTimes);
    
    return true;
  }
  
  /**
   * Reset rate limit for a key
   * @param {string} key - The key to reset
   */
  reset(key) {
    this.actions.delete(key);
  }
  
  /**
   * Clear all rate limit data
   */
  clear() {
    this.actions.clear();
  }
}

// Pre-configured rate limiters for different actions
export const postRateLimiter = new RateLimiter(10, 60000); // 10 posts per minute
export const likeRateLimiter = new RateLimiter(30, 60000); // 30 likes per minute
export const followRateLimiter = new RateLimiter(20, 60000); // 20 follows per minute

// Named exports are preferred for tree-shaking
// All functions are already exported individually above

